// src/pages/EthicalPrinciplesPage.tsx
import React, {useEffect} from "react";
import styled from "styled-components";
import {useSingleton} from "cms/factory";
import {DocumentRenderer, DocumentRendererProps} from "@keystone-6/document-renderer";
import {
    SFText18W400,
    SText24W700,
    SText24W700 as H2,
    SText40W700 as H1,
    SText40W700
} from "../Typography";

import arrowRight from "imgs/arrow_right.svg";
import ContentLayout from "../ContentLayout";

/* =============== CMS types (под Keystone lists) =============== */
type DocField = { document: DocumentRendererProps["document"] };

type CmsEthicsFaq = {
    id: string;
    order?: number | null;
    q: string;
    a?: DocField | null;
};

type CmsEthics = {
    id: string;
    title: string;
    mainText?: DocField | null;
    // hero
    heroImage?: { url?: string | null } | null;
    heroLogo?: { url?: string | null } | null;

    // gray cards
    noticeTitle: string;
    noticeText?: DocField | null;
    hotlineTitle: string;
    hotlineText?: DocField | null;
    hotlineQr?: { url?: string | null } | null;

    // faq
    faq: CmsEthicsFaq[];
};

const ETHICS_SEL = `
  id
  title
  heroImage { url }
  heroLogo  { url }
  mainText { document }
  noticeTitle
  noticeText { document }
  hotlineTitle
  hotlineText { document }
  hotlineQr { url }
  faq(orderBy: { order: asc }) {
    id order q
    a { document }
  }
`;

// безопасный фолбэк для пустых документов
const docOrEmpty = (d?: DocField | null): DocumentRendererProps["document"] =>
    (d?.document as DocumentRendererProps["document"]) ||
    ([{type: "paragraph", children: [{text: ""}]}] as DocumentRendererProps["document"]);

/* ================== Page ================== */
export default function Principles() {
    const {data, loading} = useSingleton<CmsEthics>("EthicalPrinciplesPage", ETHICS_SEL);

    useEffect(() => {
        document.title = data?.title || "Ethical Principles";
    }, [data?.title]);

    return (
        <Wrap backTo="/" loading={loading}>
            {/* HERO */}
            <Hero>
                {data?.heroImage?.url && <HeroImg style={{backgroundImage: `url(${data.heroImage.url})`}}/>}
                {data?.heroLogo?.url && (
                    <HeroLogo>
                        <img src={data.heroLogo.url} alt="logo"/>
                    </HeroLogo>
                )}
            </Hero>

            <PageTitle as="h1">{data?.title || "Ethical Principles"}</PageTitle>
            <MainText>
                <DocumentRenderer
                    document={docOrEmpty(data?.mainText)}
                    renderers={{
                        block: {
                            heading: ({level, children}) => {
                                if (level === 1) return <Title as="h1">{children}</Title>;
                                if (level === 2) return <SubTitle as="h2">{children}</SubTitle>;
                                if (level === 3) return <SubSubTitle as="h3">{children}</SubSubTitle>;
                                return <p>{children}</p>;
                            },
                            paragraph: ({children}) => <Paragraph>{children}</Paragraph>,
                        },
                        inline: {
                            link: ({children, href}) => (
                                <a href={href} className="card-link">
                                    {children}
                                </a>
                            ),
                        },
                    }}
                />
            </MainText>
            {/* ==== NOTICE (серый блок) ==== */}
            <GrayCard>
                <GrayTitle as="h2">{data?.noticeTitle || "Notice of violations"}</GrayTitle>
                <CardDoc>
                    <DocumentRenderer
                        document={docOrEmpty(data?.noticeText)}
                        renderers={{
                            block: {
                                heading: ({level, children}) => {
                                    if (level === 1) return <Title as="h1">{children}</Title>;
                                    if (level === 2) return <SubTitle as="h2">{children}</SubTitle>;
                                    if (level === 3) return <SubSubTitle as="h3">{children}</SubSubTitle>;
                                    return <p>{children}</p>;
                                },
                                paragraph: ({children}) => <Paragraph>{children}</Paragraph>,
                            },
                            inline: {
                                link: ({children, href}) => (
                                    <a href={href} className="card-link">
                                        {children}
                                    </a>
                                ),
                            },
                        }}
                    />
                </CardDoc>
            </GrayCard>

            {/* ==== HOTLINE (серый блок + QR 284px) ==== */}
            <WhiteCard>
                <GrayFlex>
                    <div style={{flex: "1 1 auto", minWidth: 0}}>
                        <GrayTitle as="h2">{data?.hotlineTitle || "Compliance Hotline"}</GrayTitle>
                        <CardDoc>
                            <DocumentRenderer
                                document={docOrEmpty(data?.hotlineText)}
                                renderers={{
                                    block: {
                                        heading: ({level, children}) => {
                                            if (level === 1) return <Title as="h1">{children}</Title>;
                                            if (level === 2) return <SubTitle as="h2">{children}</SubTitle>;
                                            if (level === 3) return <SubSubTitle as="h3">{children}</SubSubTitle>;
                                            return <p>{children}</p>;
                                        },
                                        paragraph: ({children}) => <Paragraph>{children}</Paragraph>,
                                    },
                                    inline: {
                                        link: ({children, href}) => (
                                            <a href={href} className="card-link">
                                                {children}
                                            </a>
                                        ),
                                    },
                                }}
                            />
                        </CardDoc>
                    </div>
                    {data?.hotlineQr?.url && (
                        <QrBox>
                            <img src={data.hotlineQr.url} alt="QR code"/>
                        </QrBox>
                    )}
                </GrayFlex>
            </WhiteCard>

            {/* ==== FAQ ==== */}
            <SectionTitle>FAQ</SectionTitle>
            <FaqList>
                {(data?.faq || []).map((f) => (
                    <FaqItem key={f.id}>
                        <summary>
                            <H2>{f.q}</H2>
                            <Caret src={arrowRight} alt="Back"/>
                        </summary>
                        <div className="content">
                            <DocumentRenderer
                                document={docOrEmpty(f.a)}
                                renderers={{
                                    block: {
                                        heading: ({level, children}) => {
                                            if (level === 1) return <Title as="h1">{children}</Title>;
                                            if (level === 2) return <SubTitle as="h2">{children}</SubTitle>;
                                            if (level === 3) return <SubSubTitle as="h3">{children}</SubSubTitle>;
                                            return <p>{children}</p>;
                                        },
                                        paragraph: ({children}) => <Paragraph>{children}</Paragraph>,
                                    }
                                }}
                            />
                        </div>
                    </FaqItem>
                ))}
            </FaqList>
        </Wrap>
    );
}

/* ================== styles ================== */


const Title = styled(SText40W700)`
    margin-bottom: 24px;
    text-align: left;
`;

const SubTitle = styled(SText24W700)`
    margin: 24px 0 16px;
    text-align: left;
`;

const SubSubTitle = styled(SFText18W400)`
    margin: 16px 0 12px;
    font-weight: 700;
    text-align: left;
`;

const Paragraph = styled.p`
    font-family: "GTAmerica", sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    margin: 0 0 16px;
    @media (max-width: 768px) {
        font-size: 16px;
        line-height: 20px;
    }
`;

const MainText = styled.div`
    margin: 24px 0px;
`;

const Wrap = styled(ContentLayout)`
    text-align: left;
`;

const Hero = styled.div`
    position: relative;
    width: 100%;
    height: 338px; /* фикс из ТЗ */
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 24px;
    background: #e7eef5;
`;
const HeroImg = styled.div`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background-size: 100% 100%;
    @media (max-width: 768px) {
        background-position: 100% 0px;
        background-size: 180% 100%;
    }
`;
const HeroLogo = styled.div`
    position: absolute;
    right: 85px;
    top: 70px;

    img {
        width: 254px;
        height: 74px;
        object-fit: contain;
    }

    @media (max-width: 768px) {
        img {
            width: 200px;
            height: 58px;
        }

        right: 68px;
        top: 37px;
    }
`;

const PageTitle = styled(H1)`
    margin: 24px 0;
`;

const WhiteCard = styled.section`
    margin-bottom: 16px;

    .card-link {
        color: #16a34a;
    }
`;

const GrayCard = styled.section`
    background: #f5f8fb; /* серый */
    border-radius: 16px; /* радиус */
    padding: 32px; /* внутренний отступ */
    margin-bottom: 16px;

    .card-link {
        color: #16a34a;
        text-decoration: underline;
    }
`;
const GrayTitle = styled(H2)`
    margin: 0 0 16px;
`;

const CardDoc = styled.div`
    p {
        margin: 0 0 0px;
    }

    ul {
        list-style-type: disc; /* варианты: disc | circle | square */
        list-style-position: outside;
        margin-left: 1.5rem; /* место для точек */
        padding-left: 0rem;
    }

    ul > li {
        list-style: inherit;
    }

    /* Если li превращали в block/flex/grid и пропали маркеры */

    ul > li {
        display: list-item;
    }

`;

const GrayFlex = styled.div`
    display: grid;
    grid-template-columns: 1fr 284px; /* QR 284px */
    gap: 24px;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        > div:last-child {
            justify-self: start;
        }
    }
`;
const QrBox = styled.div`
    width: 284px;
    height: 284px; /* фикс из ТЗ */
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    display: grid;
    place-items: center;
    @media (max-width: 900px) {
        margin: 0 auto;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const SectionTitle = styled(H1)`
    margin: 24px 0 12px;
`;

/* ===== FAQ (details/summary) ===== */
const FaqList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FaqItem = styled.details`
    border: 1px solid #fff; /* бордер */
    border-radius: 12px;
    background: #fff;

    /* закрытое состояние — фон #F5F8FB */

    &[open] summary {
        background: #fff;
    }

    &:not([open]) summary {
        background: #f5f8fb;
    }


    &[open] {
        border: 1px solid #ebebf2; /* бордер */
    }

    summary {
        list-style: none;
        cursor: pointer;
        padding: 16px 20px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        font-family: "GTAmerica", sans-serif;
        font-size: 18px;
        line-height: 24px;
        font-weight: 600;
    }

    summary::-webkit-details-marker {
        display: none;
    }

    .content {
        padding: 0 20px 0px;
    }

    .content ol {
        list-style-type: decimal; /* или decimal-leading-zero, lower-alpha и т.п. */
        list-style-position: outside;
        margin-left: 1.5rem; /* чтобы было место под цифры */
        padding-left: 0rem;
    }

    /* На всякий случай наследуем стиль у li */

    .content ol > li {
        list-style: inherit;
    }

    /* Если где-то было display, убивающее маркеры */

    .content ol > li {
        display: list-item;
    }
`;

const Caret = styled.img`
    width: 13px;
    height: 7px;
    flex: 0 0 13px;
    transition: transform 0.2s ease;
    transform: rotate(90deg);

    details[open] & {
        transform: rotate(270deg);
    }
`;
